import React, { useState, useRef } from 'react';
import { Check, ArrowRight, Star, Calendar, MapPin, Users, Sparkles, Crown, PartyPopper, GraduationCap, Building2, Heart, Wine, Gem, Briefcase, Sun, Music, Camera, Flame, Store, Gamepad2, Palette, Rocket, Shirt } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const eventCategories = {
  core: {
    label: 'Core Events',
    icon: Star,
    events: [
      {
        title: 'Birthday Parties',
        icon: PartyPopper,
        image: 'https://images.unsplash.com/photo-1755704282977-340323fa52df?w=600&q=80',
        description: 'Celebrate in style with a curated hookah experience designed to elevate your special day. Whether it\'s an intimate gathering or a high-energy party, we provide premium setups, top-tier flavors, and seamless service that keeps your guests engaged all night.'
      },
      {
        title: 'Graduation Parties',
        icon: GraduationCap,
        image: 'https://images.unsplash.com/photo-1755704282718-1afe3790082b?w=600&q=80',
        description: 'Mark your milestone achievement with a sophisticated and memorable experience. Our hookah catering adds a celebratory atmosphere that brings friends and family together in a relaxed, upscale environment.'
      },
      {
        title: 'Apartment Complex Events',
        icon: Building2,
        image: 'https://images.unsplash.com/photo-1768508949307-044ec3c1836a?w=600&q=80',
        description: 'Enhance your resident experience with a luxury amenity that stands out. Perfect for community mixers and tenant appreciation events, our professional setup creates a social, engaging atmosphere that residents won\'t forget.'
      }
    ]
  },
  premium: {
    label: 'Premium Events',
    icon: Crown,
    events: [
      {
        title: 'Weddings',
        subtitle: 'Receptions or After-Parties',
        icon: Heart,
        image: 'https://images.unsplash.com/photo-1763553113332-800519753e40?w=600&q=80',
        description: 'Add a unique and elegant touch to your wedding celebration. Our refined hookah service complements your reception or after-party with a relaxing, stylish experience for guests to unwind and enjoy.'
      },
      {
        title: 'Engagement Parties',
        icon: Wine,
        image: 'https://images.unsplash.com/photo-1763553113391-a659bee36e06?w=600&q=80',
        description: 'Celebrate love and new beginnings with a premium hookah setup that creates a warm and inviting atmosphere for family and friends to gather and connect.'
      },
      {
        title: 'Bachelor / Bachelorette Parties',
        icon: Sparkles,
        image: 'https://images.unsplash.com/photo-1574238752695-675b86d49267?w=600&q=80',
        description: 'Turn your celebration into an unforgettable night with a high-energy, luxury hookah experience. Perfect for private parties, nightlife vibes, and exclusive gatherings.'
      },
      {
        title: 'Private VIP Events',
        icon: Gem,
        image: 'https://images.unsplash.com/photo-1630175772812-3368aad7982d?w=600&q=80',
        description: 'Designed for exclusivity and elevated taste, our VIP hookah service delivers a fully customized, high-end experience with attention to detail, privacy, and premium presentation.'
      },
      {
        title: 'Corporate Events / Networking Mixers',
        icon: Briefcase,
        image: 'https://images.unsplash.com/photo-1768508948485-a7adc1f3427f?w=600&q=80',
        description: 'Create a refined social environment that encourages conversation and connection. Our professional setup enhances networking events with a relaxed yet upscale ambiance.'
      }
    ]
  },
  lifestyle: {
    label: 'Lifestyle & Social',
    icon: Sun,
    events: [
      {
        title: 'Day Parties / Brunches',
        icon: Sun,
        image: 'https://images.unsplash.com/photo-1558855574-7155060fc558?w=600&q=80',
        description: 'Bring a smooth, laid-back vibe to your daytime events. Our hookah setups perfectly complement brunches and day parties with a stylish and social atmosphere.'
      },
      {
        title: 'Hookah + DJ Events',
        icon: Music,
        image: 'https://images.unsplash.com/photo-1766650585020-4d290ccd19a4?w=600&q=80',
        description: 'Combine music and atmosphere for a full sensory experience. Our hookah service pairs seamlessly with live DJs to create an energetic and immersive event environment.'
      },
      {
        title: 'Lounge Nights',
        icon: Flame,
        image: 'https://images.unsplash.com/photo-1686223179141-ecab0b93bcea?w=600&q=80',
        description: 'Transform any space into a sophisticated lounge experience. Ideal for chill, upscale evenings with a focus on ambiance, comfort, and social connection.'
      },
      {
        title: 'Pop-Up Events at Venues',
        icon: Store,
        image: 'https://images.unsplash.com/photo-1583495838643-785f1fbc2498?w=600&q=80',
        description: 'Add a premium feature to any venue with our flexible pop-up hookah service. Perfect for bars, lounges, and special event nights looking to enhance their offerings.'
      },
      {
        title: 'Game Nights / Watch Parties',
        icon: Gamepad2,
        image: 'https://images.unsplash.com/photo-1764510378781-b24294c5aba5?w=600&q=80',
        description: 'Bring people together with a relaxed and engaging hookah experience that pairs perfectly with sports, games, and group entertainment.'
      }
    ]
  },
  creative: {
    label: 'Creative & Brand',
    icon: Palette,
    events: [
      {
        title: 'Influencer Events',
        icon: Camera,
        image: 'https://images.unsplash.com/photo-1768508947765-ed541d8fc6ee?w=600&q=80',
        description: 'Elevate your brand presence with a visually appealing and shareable experience. Our setups are designed to look as good as they feel, making them perfect for content creation and social media engagement.'
      },
      {
        title: 'Photoshoots / Content Creation Events',
        icon: Camera,
        image: 'https://images.unsplash.com/photo-1613909671501-f9678ffc1d33?w=600&q=80',
        description: 'Enhance your creative projects with a visually striking hookah setup. Perfect for photoshoots, videos, and branded content looking for a luxury aesthetic.'
      },
      {
        title: 'Brand Launch Parties',
        icon: Rocket,
        image: 'https://images.unsplash.com/photo-1773745060497-4cc1df774c72?w=600&q=80',
        description: 'Make a bold impression with a premium experience that aligns with your brand image. Our hookah service adds a unique and memorable element to any launch event.'
      },
      {
        title: 'Fashion Shows',
        icon: Shirt,
        image: 'https://images.unsplash.com/photo-1764158302194-54b208aa7f2b?w=600&q=80',
        description: 'Complement high-end fashion with an equally elevated atmosphere. Our sleek, stylish setups enhance the overall experience for guests, designers, and influencers alike.'
      }
    ]
  }
};

const packages = [
  {
    name: 'THE ESSENTIAL EXPERIENCE',
    tag: 'Intimate Events',
    description: 'Perfect for smaller, intimate events.',
    features: [
      'Up to 3 Hookah Setups',
      '2 Hours of Service',
      '1 On-Site Attendant',
      'Standard Flavor Selection',
      'Full Setup & Breakdown'
    ],
    accent: false
  },
  {
    name: 'THE PREMIUM EXPERIENCE',
    tag: 'Most Popular',
    description: 'Ideal for mid-size events and elevated gatherings.',
    features: [
      'Up to 5 Hookah Setups',
      '3 Hours of Service',
      '1-2 On-Site Attendants',
      'Premium Flavor Selection',
      'Enhanced Setup Presentation',
      'Customizable Experience Options'
    ],
    accent: true
  },
  {
    name: 'THE VIP LUXURY EXPERIENCE',
    tag: 'Exclusive',
    description: 'For high-end, large-scale, or exclusive events.',
    features: [
      '6+ Hookah Setups',
      '4+ Hours of Service',
      'Multiple Attendants',
      'Full Premium Flavor Menu',
      'Luxury Presentation Setup',
      'Priority Service & Custom Requests',
      'VIP Client Experience'
    ],
    accent: false
  }
];

const eventTypes = [
  'Birthday Party', 'Graduation Party', 'Apartment Complex Event',
  'Wedding', 'Engagement Party', 'Bachelor/Bachelorette Party',
  'Private VIP Event', 'Corporate Event', 'Day Party / Brunch',
  'Hookah + DJ Event', 'Lounge Night', 'Pop-Up Event',
  'Game Night / Watch Party', 'Influencer Event', 'Photoshoot / Content Creation',
  'Brand Launch Party', 'Fashion Show', 'Other'
];

const EventCard = ({ event }) => {
  const Icon = event.icon;
  return (
    <Card data-testid={`event-card-${event.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`} className="group border-gray-200 overflow-hidden hover-lift bg-white">
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={event.image}
          alt={event.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute bottom-4 left-4 right-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-full bg-[#D4AF37]/90 flex items-center justify-center">
              <Icon className="h-4 w-4 text-[#0a1128]" />
            </div>
            <h3 className="text-lg font-semibold text-white drop-shadow-lg">
              {event.title}
            </h3>
          </div>
          {event.subtitle && (
            <p className="text-sm text-gray-200 ml-10">{event.subtitle}</p>
          )}
        </div>
      </div>
      <CardContent className="p-6">
        <p className="text-gray-600 text-sm leading-relaxed mb-4">
          {event.description}
        </p>
        <Button
          data-testid={`book-now-${event.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
          className="w-full bg-[#0a1128] hover:bg-[#1e3a5f] text-white"
          onClick={() => {
            document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          Book Now
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
};

const EventCatering = () => {
  const formRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    event_date: '',
    event_type: '',
    location: '',
    guest_count: '',
    package: '',
    special_requests: ''
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/catering/book`, form);
      toast.success('Booking request submitted! Our team will contact you within 24 hours.');
      setForm({
        name: '', email: '', phone: '', event_date: '',
        event_type: '', location: '', guest_count: '',
        package: '', special_requests: ''
      });
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to submit booking. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="event-catering-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#0a1128] text-white">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 25% 50%, rgba(212, 175, 55, 0.15) 0%, transparent 50%), radial-gradient(circle at 75% 50%, rgba(212, 175, 55, 0.1) 0%, transparent 50%)'
          }} />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="max-w-3xl mx-auto text-center">
            <Badge data-testid="catering-badge" className="bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30 mb-6 text-sm px-4 py-1">
              Luxury Hookah Catering
            </Badge>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Elevate Your Event with a
              <span className="text-[#D4AF37]"> Premium Hookah</span> Experience
            </h1>
            <p className="text-base md:text-lg text-gray-300 mb-8 leading-relaxed max-w-2xl mx-auto">
              From intimate gatherings to high-energy celebrations, we deliver a seamless, upscale atmosphere your guests will remember. Positioning your event as nothing less than extraordinary.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                data-testid="hero-book-btn"
                size="lg"
                className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0a1128] font-semibold h-14 px-8"
                onClick={() => document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Book Your Experience
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                data-testid="hero-packages-btn"
                variant="outline"
                size="lg"
                className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0a1128] h-14 px-8"
                onClick={() => document.getElementById('pricing-packages')?.scrollIntoView({ behavior: 'smooth' })}
              >
                View Packages
              </Button>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
      </section>

      {/* Event Categories with Tabs */}
      <section className="py-20 bg-white" data-testid="event-categories-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#0a1128] mb-4">
              Our Event Services
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              We cater to a wide range of events, each with a tailored luxury hookah experience
            </p>
          </div>

          <Tabs defaultValue="core" className="w-full">
            <TabsList data-testid="event-tabs" className="flex flex-wrap justify-center gap-2 bg-transparent mb-12 h-auto">
              {Object.entries(eventCategories).map(([key, cat]) => {
                const CatIcon = cat.icon;
                return (
                  <TabsTrigger
                    key={key}
                    value={key}
                    data-testid={`tab-${key}`}
                    className="data-[state=active]:bg-[#0a1128] data-[state=active]:text-white px-6 py-3 rounded-full border border-gray-200 data-[state=active]:border-[#0a1128] text-sm font-medium transition-all"
                  >
                    <CatIcon className="h-4 w-4 mr-2" />
                    {cat.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {Object.entries(eventCategories).map(([key, cat]) => (
              <TabsContent key={key} value={key} data-testid={`tabcontent-${key}`}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                  {cat.events.map((event, idx) => (
                    <EventCard key={idx} event={event} />
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Book Your Luxury Experience CTA */}
      <section className="py-20 bg-gradient-to-br from-[#0a1128] to-[#1e3a5f] text-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-4xl sm:text-5xl font-bold mb-6">
            Book Your Luxury Hookah Experience
          </h2>
          <p className="text-base md:text-lg text-gray-300 mb-4 leading-relaxed">
            Elevate your next event with a premium hookah experience designed to impress. From intimate gatherings to high-energy celebrations, we deliver a seamless, upscale atmosphere your guests will remember.
          </p>
          <div className="inline-flex items-center gap-2 bg-[#D4AF37]/20 border border-[#D4AF37]/30 rounded-full px-6 py-2 mb-8">
            <Calendar className="h-4 w-4 text-[#D4AF37]" />
            <span className="text-[#D4AF37] font-medium text-sm">Limited Availability — Secure Your Date Today</span>
          </div>
          <p className="text-gray-400 text-sm mb-8">
            We accept a limited number of bookings to ensure each event receives the highest level of service and attention.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg mx-auto text-left">
            {[
              'Professional Setup & Breakdown',
              'Premium Hookah Equipment & Flavors',
              'On-Site Attendant(s)',
              'Customized Experience Based on Your Event'
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                  <Check className="h-3 w-3 text-[#D4AF37]" />
                </div>
                <span className="text-gray-200 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Packages */}
      <section id="pricing-packages" className="py-20 bg-gray-50" data-testid="pricing-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30 mb-4 text-sm px-4 py-1">
              Tailored to Your Event
            </Badge>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#0a1128] mb-4">
              Experience Packages
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the experience level that matches your vision — we'll customize the details for you
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {packages.map((pkg, idx) => (
              <Card
                key={idx}
                data-testid={`package-card-${idx}`}
                className={`relative overflow-hidden transition-all duration-300 hover:-translate-y-2 ${
                  pkg.accent
                    ? 'border-2 border-[#D4AF37] shadow-xl shadow-[#D4AF37]/10'
                    : 'border-gray-200'
                }`}
              >
                {pkg.accent && (
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#D4AF37] via-[#FFD700] to-[#D4AF37]" />
                )}
                {pkg.accent && (
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-[#D4AF37] text-[#0a1128] font-semibold">Most Popular</Badge>
                  </div>
                )}
                <CardContent className="p-8">
                  <p className="text-xs font-medium text-[#D4AF37] uppercase tracking-wider mb-2">{pkg.tag}</p>
                  <h3 className="text-2xl font-bold text-[#0a1128] mb-2 font-display">{pkg.name}</h3>
                  <p className="text-gray-500 text-sm mb-6">{pkg.description}</p>
                  <div className="space-y-3 mb-8">
                    {pkg.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-5 h-5 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                          <Check className="h-3 w-3 text-[#D4AF37]" />
                        </div>
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Button
                    data-testid={`package-select-${idx}`}
                    className={`w-full ${
                      pkg.accent
                        ? 'bg-[#D4AF37] hover:bg-[#C5A028] text-[#0a1128]'
                        : 'bg-[#0a1128] hover:bg-[#1e3a5f] text-white'
                    }`}
                    onClick={() => {
                      setForm(prev => ({ ...prev, package: pkg.name }));
                      document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    Request a Quote
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section id="booking-form" className="py-20 bg-white" data-testid="booking-form-section">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#0a1128] mb-4">
              Request a Quote
            </h2>
            <p className="text-base md:text-lg text-gray-600">
              Fill out the form below and our team will contact you within 24 hours to confirm availability and customize your experience.
            </p>
          </div>

          <Card className="border-gray-200 shadow-lg">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} ref={formRef} data-testid="booking-form" className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name</label>
                    <Input
                      data-testid="booking-name"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      required
                      className="h-12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                    <Input
                      data-testid="booking-email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      required
                      className="h-12"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone</label>
                    <Input
                      data-testid="booking-phone"
                      name="phone"
                      type="tel"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                      required
                      className="h-12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Event Date</label>
                    <Input
                      data-testid="booking-date"
                      name="event_date"
                      type="date"
                      value={form.event_date}
                      onChange={handleChange}
                      required
                      className="h-12"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Event Type</label>
                    <select
                      data-testid="booking-event-type"
                      name="event_type"
                      value={form.event_type}
                      onChange={handleChange}
                      required
                      className="w-full h-12 rounded-md border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a1128] focus:border-transparent"
                    >
                      <option value="">Select event type</option>
                      {eventTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Number of Guests</label>
                    <Input
                      data-testid="booking-guests"
                      name="guest_count"
                      value={form.guest_count}
                      onChange={handleChange}
                      placeholder="Estimated guest count"
                      required
                      className="h-12"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Location</label>
                  <Input
                    data-testid="booking-location"
                    name="location"
                    value={form.location}
                    onChange={handleChange}
                    placeholder="Event venue or address"
                    required
                    className="h-12"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Preferred Package</label>
                  <select
                    data-testid="booking-package"
                    name="package"
                    value={form.package}
                    onChange={handleChange}
                    className="w-full h-12 rounded-md border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a1128] focus:border-transparent"
                  >
                    <option value="">Select a package (optional)</option>
                    {packages.map((pkg) => (
                      <option key={pkg.name} value={pkg.name}>{pkg.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Special Requests</label>
                  <textarea
                    data-testid="booking-special-requests"
                    name="special_requests"
                    value={form.special_requests}
                    onChange={handleChange}
                    placeholder="Any special requirements or preferences..."
                    rows={4}
                    className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a1128] focus:border-transparent resize-none"
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button
                    data-testid="booking-submit"
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-[#D4AF37] hover:bg-[#C5A028] text-[#0a1128] font-semibold h-14"
                  >
                    {submitting ? 'Submitting...' : 'Request a Quote'}
                    {!submitting && <ArrowRight className="ml-2 h-5 w-5" />}
                  </Button>
                  <Button
                    data-testid="booking-check-availability"
                    type="submit"
                    disabled={submitting}
                    variant="outline"
                    className="flex-1 border-[#0a1128] text-[#0a1128] hover:bg-[#0a1128] hover:text-white h-14"
                  >
                    Check Availability
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#0a1128] text-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="flex -space-x-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-6 w-6 text-[#D4AF37] fill-current" />
              ))}
            </div>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold mb-6">
            Ready to Create an Unforgettable Experience?
          </h2>
          <p className="text-base md:text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
            Join the growing list of satisfied clients who trust Smithhookahsupplyco for their premium event hookah needs.
          </p>
          <Button
            data-testid="final-cta-btn"
            size="lg"
            className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0a1128] font-semibold h-14 px-8"
            onClick={() => document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Book Your Experience
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default EventCatering;
